import { Skeleton } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { transferQueueStats } from '#lib/react-query/queries.ts';
import { STAT_WIDGET_CHART_HEIGHT } from '#utils/charts.ts';
import { formatDurationFromSeconds } from '#utils/dates.ts';
import { Base } from '../Widgets/Base.tsx';
import { StatBase } from '../Widgets/Stats/StatBase.tsx';
import { useTransferQueue } from './use-transfer-queue.ts';

const statDefinitions = [
  { id: 'entered', title: 'Entered Queue', valueKey: 'enteredCount', showTrend: false },
  { id: 'exited', title: 'Exited Queue', valueKey: 'exitedCount', showTrend: false },
  {
    id: 'currently-queued',
    title: 'Currently Queued',
    valueKey: 'currentlyQueuedCount',
    showTrend: false,
  },
] as const;

const loadingStatIds = [
  'entered',
  'exited',
  'currently-queued',
  'average-wait',
  'longest-wait',
  'patient-disconnections',
] as const;

export function TransferQueueStats() {
  const transferQueue = useTransferQueue();
  const query = useQuery(transferQueueStats(transferQueue.getRequestParams()));
  const daysInPeriod = transferQueue.homeData.totalDaysInRange;

  if (query.isLoading) {
    return loadingStatIds.map((id) => (
      <Base key={id} title="Transfer Queue metric">
        <Skeleton variant="rounded" height={STAT_WIDGET_CHART_HEIGHT} />
      </Base>
    ));
  }

  const stats = query.data;

  return (
    <>
      {statDefinitions.map((definition) => (
        <StatBase
          key={definition.id}
          id={`transfer-queue-${definition.id}`}
          data={{
            id: `transfer-queue-${definition.id}`,
            title: definition.title,
            value: stats?.[definition.valueKey] ?? null,
            valueType: 'absolute',
          }}
          showTrend={definition.showTrend}
        />
      ))}

      <StatBase
        id="transfer-queue-average-wait"
        data={{
          id: 'transfer-queue-average-wait',
          title: 'Average Wait',
          value: stats?.averageWaitSeconds ?? null,
          valueFormatted:
            stats?.averageWaitSeconds == null
              ? undefined
              : formatDurationFromSeconds(stats.averageWaitSeconds),
          valueType: 'absolute',
          trend: stats?.averageWaitTrend ? { ...stats.averageWaitTrend, daysInPeriod } : undefined,
        }}
        isLowerBetter
      />

      <StatBase
        id="transfer-queue-longest-wait"
        data={{
          id: 'transfer-queue-longest-wait',
          title: 'Longest Completed Wait',
          value: stats?.longestCompletedWaitSeconds ?? null,
          valueFormatted:
            stats?.longestCompletedWaitSeconds == null
              ? undefined
              : formatDurationFromSeconds(stats.longestCompletedWaitSeconds),
          valueType: 'absolute',
          trend: stats?.longestCompletedWaitTrend
            ? { ...stats.longestCompletedWaitTrend, daysInPeriod }
            : undefined,
        }}
        isLowerBetter
      />

      <StatBase
        id="transfer-queue-patient-disconnections"
        data={{
          id: 'transfer-queue-patient-disconnections',
          title: 'Patient Disconnections',
          value: stats?.patientDisconnectedCount ?? null,
          valueFormatted:
            stats === undefined
              ? undefined
              : stats.patientDisconnectedRate === null
                ? String(stats.patientDisconnectedCount)
                : `${stats.patientDisconnectedCount} (${stats.patientDisconnectedRate.toFixed(1)}%)`,
          valueType: 'absolute',
          trend: stats?.patientDisconnectedTrend
            ? { ...stats.patientDisconnectedTrend, daysInPeriod }
            : undefined,
        }}
        isLowerBetter
      />
    </>
  );
}
